import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Txt } from "@/components/Txt";
import { RolePickerModal } from "@/components/checkin/RolePicker";

const fmtDT = (s) => (s ? new Date(s).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

function EventCard({ ev, onRsvp }) {
  const [bigFlyer, setBigFlyer] = useState(false);
  const [rolePick, setRolePick] = useState(null);
  const set = async (response) => {
    const next = ev.my_rsvp === response ? "none" : response;
    try { await api.post(`/events/${ev.event_id}/rsvp`, { response: next }); onRsvp(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not RSVP"); }
  };
  const checkin = async () => {
    try {
      const r = await api.post("/checkin/self", { event_id: ev.event_id });
      if (r.data.needs_roles) { setRolePick(r.data); return; }
      toast.success("You're checked in!"); onRsvp();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Could not check in"); }
  };
  const claim = async (roleId, mine) => {
    try {
      if (mine) await api.delete(`/events/${ev.event_id}/volunteer/${roleId}/claim`);
      else await api.post(`/events/${ev.event_id}/volunteer/${roleId}/claim`);
      onRsvp();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const respond = async (r) => {
    try { await api.post(`/events/${ev.event_id}/invite/respond`, { response: r }); toast.success("Thanks for responding"); onRsvp(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not respond"); }
  };
  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/events/${ev.event_id}/photos`, fd); toast.success("Photo uploaded"); onRsvp(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5 space-y-3" data-testid={`event-card-${ev.event_id}`}>
      <RolePickerModal data={rolePick} onClose={() => setRolePick(null)}
        onDone={() => { setRolePick(null); toast.success("You're checked in!"); onRsvp(); }} />
      <div>
        <div className="text-[12px] uppercase tracking-widest text-dark-olive font-bold flex items-center gap-1.5"><Icon name="Calendar" className="w-3.5 h-3.5" />{fmtDT(ev.start)}{ev.end ? ` – ${new Date(ev.end).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : ""}</div>
        <h3 className="font-zilla text-xl font-semibold mt-1">{ev.title}</h3>
        {ev.description && <p className="text-[15px] text-ink/70 mt-1 whitespace-pre-wrap">{ev.description}</p>}
      </div>

      {ev.flyer_url && (
        <button type="button" data-testid={`event-flyer-${ev.event_id}`} onClick={() => setBigFlyer(true)} className="block w-full rounded-lg overflow-hidden border border-ink/15 hover:opacity-90">
          <img src={ev.flyer_url} alt={`${ev.title} flyer`} className="w-full max-h-72 object-contain bg-warm-cream" />
        </button>
      )}
      {bigFlyer && ev.flyer_url && createPortal(
        <div className="fixed inset-0 z-[120] bg-ink/90 flex items-center justify-center p-4" onClick={() => setBigFlyer(false)} data-testid={`event-flyer-lightbox-${ev.event_id}`}>
          <img src={ev.flyer_url} alt={`${ev.title} flyer`} className="max-h-full max-w-full object-contain" />
          <button data-testid="event-flyer-lightbox-close" onClick={() => setBigFlyer(false)} className="absolute top-4 right-4 text-cream bg-ink/60 rounded-full w-10 h-10 flex items-center justify-center"><Icon name="X" className="w-5 h-5" /></button>
        </div>, document.body)}

      {(ev.tags || []).length > 0 && (
        <div className="flex gap-1.5 flex-wrap">{ev.tags.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-olive/15 border border-olive/30">{t}</span>)}</div>
      )}

      <div className="flex flex-wrap gap-2">
        {ev.address && <a data-testid={`event-directions-${ev.event_id}`} href={mapsUrl(ev.address)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-ink/20 rounded-lg px-3 py-2 min-h-[44px] text-[14px] font-semibold"><Icon name="MapPin" className="w-4 h-4 text-maroon" /><Txt id="events.rsvp.directions" screen="Member events">Get directions</Txt></a>}
        {ev.video_link && <a data-testid={`event-join-${ev.event_id}`} href={ev.video_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-ink/20 rounded-lg px-3 py-2 min-h-[44px] text-[14px] font-semibold"><Icon name="Video" className="w-4 h-4 text-dark-olive" /><Txt id="events.rsvp.join" screen="Member events">Join the video call</Txt></a>}
      </div>

      <div className="flex gap-2 pt-1 border-t border-ink/8">
        <button data-testid={`rsvp-going-${ev.event_id}`} onClick={() => set("going")} className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 min-h-[44px] text-[14px] font-semibold border ${ev.my_rsvp === "going" ? "bg-olive text-ink border-olive" : "border-ink/25"}`}><Icon name="Check" className="w-4 h-4" /><Txt id="events.rsvp.going" screen="Member events">I'm going</Txt></button>
        <button data-testid={`rsvp-cant-${ev.event_id}`} onClick={() => set("cant")} className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 min-h-[44px] text-[14px] font-semibold border ${ev.my_rsvp === "cant" ? "bg-ink text-cream border-ink" : "border-ink/25"}`}><Icon name="X" className="w-4 h-4" /><Txt id="events.rsvp.cant" screen="Member events">Can't make it</Txt></button>
      </div>
      <div className="text-[12px] text-ink/50">{ev.rsvp_counts?.going || 0} going</div>

      {ev.my_checked_in && !ev.ended ? (
        <div className="inline-flex items-center gap-1.5 text-dark-olive font-semibold text-[14px]" data-testid={`checked-in-${ev.event_id}`}><Icon name="Check" className="w-4 h-4" /> You're checked in</div>
      ) : (ev.checkin_open && ev.my_rsvp === "going" && (
        <Button data-testid={`self-checkin-${ev.event_id}`} onClick={checkin} className="w-full"><Icon name="QrCode" className="w-4 h-4" /> Check in now</Button>
      ))}

      {(ev.volunteer_roles || []).length > 0 && (
        <div className="pt-2 border-t border-ink/8" data-testid={`volunteer-roles-${ev.event_id}`}>
          <div className="text-[12px] uppercase tracking-wide text-ink/55 font-semibold mb-2">Volunteer roles</div>
          <div className="space-y-2">
            {ev.volunteer_roles.map((r) => (
              <div key={r.role_id} data-testid={`role-${r.role_id}`} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold flex items-center gap-2">{r.name}{r.points ? <span data-testid={`role-points-badge-${r.role_id}`} className="text-[11px] text-dark-olive bg-olive/15 rounded-full px-2 py-0.5 font-semibold">{r.points} pts</span> : null}</div>
                  <div className="text-[12px] text-ink/50">{r.claimed}/{r.needed} filled{r.hours ? ` · ${r.hours}h` : ""}{r.start ? ` · ${new Date(r.start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : ""}</div>
                </div>
                {r.my_claim ? (
                  <Button variant="secondary" data-testid={`release-${r.role_id}`} onClick={() => claim(r.role_id, true)} className="shrink-0 px-3 py-1.5 min-h-[38px] text-[13px]">Release</Button>
                ) : (
                  <Button variant="olive" data-testid={`claim-${r.role_id}`} disabled={r.spots_left === 0} onClick={() => claim(r.role_id, false)} className="shrink-0 px-3 py-1.5 min-h-[38px] text-[13px]">{r.spots_left === 0 ? "Full" : "Claim"}</Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {ev.my_invitation && (
        <div className="pt-2 border-t border-ink/8" data-testid={`invitation-${ev.event_id}`}>
          <div className="text-[12px] uppercase tracking-wide text-maroon font-semibold mb-1">You're invited to help</div>
          {ev.my_invitation.message && <p className="text-[14px] text-ink/75 mb-2">{ev.my_invitation.message}</p>}
          {ev.my_invitation.status === "awaiting" ? (
            <div className="flex gap-2">
              <Button variant="olive" data-testid={`invite-yes-${ev.event_id}`} onClick={() => respond("yes")} className="flex-1 px-3 py-2 min-h-[42px] text-[14px]">Yes, I'll help</Button>
              <Button variant="secondary" data-testid={`invite-no-${ev.event_id}`} onClick={() => respond("no")} className="flex-1 px-3 py-2 min-h-[42px] text-[14px]">Can't</Button>
            </div>
          ) : (
            <div data-testid={`invite-answered-${ev.event_id}`} className="text-[14px] font-semibold">{ev.my_invitation.status === "yes" ? "You said yes — thank you!" : "You declined this one."} <button onClick={() => respond(ev.my_invitation.status === "yes" ? "no" : "yes")} className="text-dark-olive underline font-normal ml-1">change</button></div>
          )}
        </div>
      )}

      {ev.i_checked_in_ever && (
        <div className="pt-2 border-t border-ink/8">
          <label data-testid={`upload-photos-${ev.event_id}`} className="inline-flex items-center gap-1.5 bg-dark-olive text-cream rounded-lg px-3 py-2.5 min-h-[44px] text-[14px] font-semibold cursor-pointer">
            <Icon name="Image" className="w-4 h-4" /> Upload photos
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} />
          </label>
          {ev.photo_count > 0 && <span className="text-[12px] text-ink/50 ml-2">{ev.photo_count} photo{ev.photo_count === 1 ? "" : "s"} so far</span>}
        </div>
      )}
    </div>
  );
}

export default function MemberEvents() {
  const { persona } = useAuth();
  const [data, setData] = useState(undefined);
  const load = useCallback(async () => {
    const r = await api.get("/events/calendar/feed", { params: persona ? { persona } : {} });
    setData(r.data.items);
  }, [persona]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="member-events-page" className="space-y-4">
      <div>
        <h1 className="font-zilla text-3xl font-semibold"><Txt id="events.calendar.title" screen="Member events">Events</Txt></h1>
        <p className="text-[16px] text-ink/70"><Txt id="events.calendar.subtitle" screen="Member events">What's coming up at Rosemoor.</Txt></p>
      </div>
      {data === undefined ? (
        <div className="text-ink/60 py-8">Loading…</div>
      ) : data.length === 0 ? (
        <div className="bg-cream border border-ink/15 rounded-xl p-10 text-center text-ink/55" data-testid="member-events-empty">
          <Txt id="events.calendar.empty" screen="Member events">No upcoming events right now. Check back soon.</Txt>
        </div>
      ) : (
        data.map((ev) => <EventCard key={ev.event_id} ev={ev} onRsvp={load} />)
      )}
    </div>
  );
}
